/* eslint-disable react/no-this-in-sfc */
import GKCharts from "components/GKCharts";
import Highcharts from "highcharts/highstock";
import { useMemo } from "react";
import { formatYAxisLabel, handleReturnData } from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";

interface ChartDataInterface {
  holdings: [number, number][];
  payIn: [number, number][];
  payOut: [number, number][];
  total: [number, number][];
  equity: [number, number][];
  mutualFunds: [number, number][];
  privateEquity: [number, number][];
  fd: [number, number][];
  aif: [number, number][];
  ledgerBalances: [number, number][];
}

interface Props {
  loading: boolean;
  data: ChartDataInterface;
}

const PortfolioSummaryMultiLineChart = (props: Props) => {
  const { data, loading } = props;

  const chartDataOption = useMemo(() => {
    return {
      chart: {
        borderRadius: 5,
        backgroundColor: "var(--white)",
        height: 650,
        spacing: [10, 10, 10, 10],
        marginRight: 50,
      },
      tooltip: {
        formatter(this: any) {
          const formattedDate = Highcharts.dateFormat("%b %e, %Y", this.x);

          let content = `<b>${formattedDate}</b><br/>`;

          // show all plotted series
          (this.points || []).forEach((point: any) => {
            content += `<span style="color:${point.color}">\u25CF</span> ${
              point.series.name
            }: <b>${moneyCommaSeparator(point.y)}</b><br/>`;
          });

          // 🔹 Fetch ledger balance directly from API (not plotted)
          const ledgerBalancePoint = props.data?.ledgerBalances?.find(
            (p) => p[0] === this.x, // match timestamp
          );

          if (ledgerBalancePoint) {
            const ledgerBalanceValue = ledgerBalancePoint[1];
            content += `<span style="color:var(--secondary-900)">\u25CF</span> Ledger Balance: <b>${moneyCommaSeparator(
              ledgerBalanceValue,
            )}</b><br/>`;
          }

          return content;
        },
        pointFormatter(this: any) {
          return `<span style="color:${this?.series?.color}">${
            this?.series?.name
          }</span>: ${moneyCommaSeparator(this?.y)}<br/>`;
        },
        valueDecimals: 2,
        split: true,
      },
      title: {
        text: "",
        enabled: false,
        align: "left",
        style: {
          color: "var(--primary-900)",
          fontWeight: "bold",
          fontSize: 18,
        },
      },
      legend: {
        enabled: true,
        layout: "horizontal",
        align: "center",
        verticalAlign: "top",
        itemStyle: {
          color: "var(--black)",
          fontWeight: "bold",
        },
        symbolWidth: 20,
        symbolHeight: 10,
      },
      rangeSelector: {
        inputStyle: {
          color: "var(--primary-900)",
          fontWeight: "normal",
        },
        labelStyle: {
          color: "var(--primary-900)",
          fontWeight: "normal",
        },
        buttonTheme: {
          width: 50,
          fill: `#ffffff`,
          style: {
            background: "var(--white)",
            color: "var(--neutral-500)",
            fontWeight: "normal",
            textTransform: "uppercase",
          },
          states: {
            select: {
              fill: `#F8FAFC`,
              style: {
                fontWeight: "normal",
                color: "var(--neutral-500)",
              },
            },
          },
        },
      },
      plotOptions: {
        series: {
          states: {
            hover: {
              enabled: false,
            },
          },
          animation: false,
        },
      },
      xAxis: {
        type: "date",
        dateTimeLabelFormats: {
          day: "%b %e, %Y",
          week: "%b %e, %Y",
          month: "%b %Y",
          year: "%Y",
        },
        labels: {
          style: {
            color: "var(--primary-900)",
            fontSize: "12px",
            fontWeight: "bold",
          },
          formatter(this: any) {
            return Highcharts.dateFormat("%b %e, %Y", this.value);
          },
        },
      },
      yAxis: {
        opposite: false,
        gridLineColor: `var(--neutral-100)`,
        color: `var(--warning-700)`,
        min: 0,
        labels: {
          style: {
            color: "var(--primary-900)",
            fontSize: "12px",
            fontWeight: "bold",
          },
          formatter(this: any) {
            return formatYAxisLabel(this?.value);
          },
        },
      },
      series: [
        {
          name: "Portfolio Value",
          data: handleReturnData(data?.total),
          color: "var(--primary-900)",
          fillOpacity: 0.5,
        },
        {
          name: "Equity",
          data: handleReturnData(data?.equity),
          color: "#63a387",
        },
        {
          name: "Mutual Fund",
          data: handleReturnData(data?.mutualFunds),
          color: "#EA7017",
        },
        {
          name: "Private Equity",
          data: data?.privateEquity || [],
          color: "#8D51C5",
        },
        {
          name: "Fixed Deposit",
          data: data?.fd || [],
          color: "#C551A5",
        },
        {
          name: "AIF",
          data: data?.aif || [],
          color: "#F8CD38",
        },
      ],
    };
  }, [data]);

  return (
    <GKCharts
      chartOptions={chartDataOption}
      rangeSelector
      loading={loading}
      navigator
    />
  );
};

export default PortfolioSummaryMultiLineChart;
